/**
 * Firestore → Supabase compatibility shim.
 *
 * Emulates the slice of the `firebase/firestore` API the admin + POS
 * pages actually use, but routes every read/write to Supabase. Lets
 * pages keep their existing `collection(db, 'orders')` / `getDocs` /
 * `onSnapshot` / `updateDoc` style without a full rewrite.
 *
 * Drop-in replacement — change the import path:
 *   - from 'firebase/firestore'  →  from '../../services/firestoreCompat'
 *
 * Field shapes:
 *   - We assume the Firestore collection name matches the Supabase
 *     table name (which is the case for everything Sushiki originally
 *     wrote: orders, restaurant_tables, drivers, customers,
 *     menu_items, etc.). For mismatches we map via COLLECTION_MAP.
 *   - `serverTimestamp()`   → ISO timestamp string (now)
 *   - `deleteField()`       → null on update payloads
 *   - `Timestamp.now()`     → ISO string (Date)
 *
 * What we DO NOT emulate:
 *   - Subcollections (none used in this codebase).
 *   - `runTransaction` / `writeBatch` — callers fall back to
 *     sequential updates (already what they did under offline mode).
 *   - Strong type safety on the snapshot objects — they're typed
 *     `any`-ish so existing call sites that destructure `d.data()`
 *     into untyped objects keep working.
 */

import { supabase } from './supabaseClient';

// ─── Collection name remap (Firestore name → Supabase table) ────────────────
// Most names match 1:1. Anything that diverges goes here.
const COLLECTION_MAP: Record<string, string> = {
  users:       'staff_users',     // legacy 'users' = admin staff in Firestore
  admin_users: 'staff_users',
  pos_users:   'staff_users',
  pos_shifts:  'shifts',
  dishes:      'menu_items',      // legacy alias
};

function tableFor(name: string): string {
  return COLLECTION_MAP[name] || name;
}

// ─── Sentinel for deleteField() ─────────────────────────────────────────────
const DELETE_FIELD_SENTINEL = Symbol('firestoreCompat.deleteField');

export function deleteField(): any {
  return DELETE_FIELD_SENTINEL;
}

export function serverTimestamp(): string {
  return new Date().toISOString();
}

export const Timestamp = {
  now: () => new Date().toISOString(),
  fromDate: (d: Date) => d.toISOString(),
  fromMillis: (ms: number) => new Date(ms).toISOString(),
};
/** Type alias — Firestore had a `Timestamp` class with toMillis/toDate
 *  methods. Real values are ISO strings at runtime, but legacy admin code
 *  occasionally calls `.toMillis()` inside try/catch blocks. Typing it as
 *  the union keeps TS happy while the try/catch handles the runtime case. */
// eslint-disable-next-line no-redeclare
export type Timestamp = string | { toMillis(): number; toDate(): Date };

/** No-op SerialIncrement — Supabase auto-handles updated_at via triggers. */
export function increment(by: number): any {
  // Returns a sentinel; updateDoc converts it. Used rarely in this codebase.
  return { __increment: by };
}

/** Firestore-shaped snapshot types — loose payloads kept as `any`. */
export interface DocSnap {
  id: string;
  exists: () => boolean;
  data: () => any;
  ref: DocRef;
}
/** Single doc change (Firestore's docChanges() entry). We always emit
 *  'modified' since we re-read the whole snap on every Realtime event. */
export interface DocChange {
  type: 'added' | 'modified' | 'removed';
  doc: DocSnap;
}

export interface QuerySnap {
  docs: DocSnap[];
  empty: boolean;
  size: number;
  forEach: (cb: (d: DocSnap) => void) => void;
  docChanges: () => DocChange[];
}

/** No-op getFirestore for callers that imported it just to keep TS happy.
 *  The shim doesn't need a real db instance — the `db` arg is ignored. */
export function getFirestore(_app?: any): any { return null; }

/** No-op disableNetwork — Firebase pages call this to put Firestore into
 *  offline mode when env is unset. Supabase has no equivalent state; just
 *  resolve. */
export async function disableNetwork(_db?: any): Promise<void> {}
export async function enableNetwork(_db?: any): Promise<void> {}

/** Subscription handle type expected by callers. */
export type Unsubscribe = () => void;

/** Aliases that mirror the original Firestore SDK type names so callers
 *  that imported them by name (QuerySnapshot, DocumentSnapshot, etc.) keep
 *  compiling without any rename. */
export type DocumentData        = Record<string, any>;
export type DocumentReference   = DocRef;
export type CollectionReference = CollRef;
// Generic-shaped aliases — Firestore SDK had `QuerySnapshot<T>` etc.
// We don't use the generic, but accept it so old call-sites compile.
export type QuerySnapshot<_T = any>    = QuerySnap;
export type DocumentSnapshot<_T = any> = DocSnap;

// ─── Internal query types ───────────────────────────────────────────────────

type Op = '==' | '!=' | '<' | '<=' | '>' | '>=' | 'in' | 'not-in' | 'array-contains' | 'array-contains-any';

interface WhereClause { field: string; op: Op; value: any }
interface OrderByClause { field: string; dir: 'asc' | 'desc' }

class CollRef {
  constructor(public name: string) {}
  __isColl = true as const;
}

export class DocRef {
  constructor(public collection: string, public id?: string) {}
  __isDoc = true as const;
}

class Q {
  constructor(
    public collection: string,
    public wheres: WhereClause[] = [],
    public orders: OrderByClause[] = [],
    public limitN: number | null = null,
  ) {}
  __isQuery = true as const;
}

// ─── Builders ──────────────────────────────────────────────────────────────

export function collection(_db: any, name: string): CollRef {
  return new CollRef(name);
}

export function doc(_db: any, name: string, id?: string): DocRef;
export function doc(coll: CollRef, id?: string): DocRef;
export function doc(...args: any[]): DocRef {
  if (args[0] && args[0].__isColl) {
    return new DocRef(args[0].name, args[1]);
  }
  return new DocRef(args[1], args[2]);
}

export function where(field: string, op: Op, value: any): WhereClause {
  return { field, op, value };
}

export function orderBy(field: string, dir: 'asc' | 'desc' = 'asc'): OrderByClause {
  return { field, dir };
}

export function limit(n: number): { __isLimit: true; n: number } {
  return { __isLimit: true, n };
}

export function query(source: CollRef | Q, ...clauses: any[]): Q {
  const base = source instanceof Q ? source : new Q(source.name);
  const next = new Q(base.collection, [...base.wheres], [...base.orders], base.limitN);
  for (const c of clauses) {
    if (!c) continue;
    if ((c as WhereClause).op) next.wheres.push(c as WhereClause);
    else if ((c as OrderByClause).dir) next.orders.push(c as OrderByClause);
    else if ((c as any).__isLimit) next.limitN = (c as any).n;
  }
  return next;
}

// ─── Query → Supabase ──────────────────────────────────────────────────────

function applyClauses(q: Q): any {
  let chain: any = supabase.from(tableFor(q.collection)).select('*');
  for (const w of q.wheres) {
    const val = w.value;
    switch (w.op) {
      case '==':  chain = val === null ? chain.is(w.field, null) : chain.eq(w.field, val);  break;
      case '!=':  chain = val === null ? chain.not(w.field, 'is', null) : chain.neq(w.field, val); break;
      case '<':   chain = chain.lt(w.field, val);  break;
      case '<=':  chain = chain.lte(w.field, val); break;
      case '>':   chain = chain.gt(w.field, val);  break;
      case '>=':  chain = chain.gte(w.field, val); break;
      case 'in':           chain = chain.in(w.field, val as any[]); break;
      case 'not-in':       chain = chain.not(w.field, 'in', `(${(val as any[]).map((v) => JSON.stringify(v)).join(',')})`); break;
      case 'array-contains':     chain = chain.contains(w.field, [val]); break;
      case 'array-contains-any': chain = chain.overlaps(w.field, val as any[]); break;
      default: console.warn('[firestoreCompat] unsupported op:', w.op);
    }
  }
  for (const o of q.orders) {
    chain = chain.order(o.field, { ascending: o.dir === 'asc' });
  }
  if (q.limitN != null) chain = chain.limit(q.limitN);
  return chain;
}

// ─── Snapshot wrappers (Firestore-shaped) ──────────────────────────────────

function wrapDoc(table: string, row: any): any {
  const id = row?.id ?? '';
  return {
    id,
    exists: () => row != null,
    data: () => row,
    ref: new DocRef(table, id),
  };
}

function wrapQuerySnap(table: string, rows: any[]): QuerySnap {
  const docs: DocSnap[] = (rows || []).map((r) => wrapDoc(table, r));
  return {
    docs,
    empty: docs.length === 0,
    size: docs.length,
    forEach: (cb: (d: DocSnap) => void) => docs.forEach(cb),
    // We don't compute per-doc diffs — every Realtime event triggers a
    // full re-read, so we emit each doc as 'modified'. Callers that only
    // care about new docs should switch to `.docs` directly.
    docChanges: () => docs.map((d) => ({ type: 'modified', doc: d })),
  };
}

// ─── Reads ─────────────────────────────────────────────────────────────────

export async function getDocs(source: CollRef | Q): Promise<QuerySnap> {
  const q = source instanceof Q ? source : new Q(source.name);
  const { data, error } = await applyClauses(q);
  if (error) {
    console.error('[firestoreCompat] getDocs error:', error);
    return wrapQuerySnap(q.collection, []);
  }
  return wrapQuerySnap(q.collection, data || []);
}

export async function getDoc(ref: DocRef): Promise<DocSnap> {
  if (!ref.id) return wrapDoc(ref.collection, null);
  const { data, error } = await supabase
    .from(tableFor(ref.collection))
    .select('*')
    .eq('id', ref.id)
    .maybeSingle();
  if (error) {
    console.error('[firestoreCompat] getDoc error:', error);
    return wrapDoc(ref.collection, null);
  }
  return wrapDoc(ref.collection, data);
}

// ─── Writes ────────────────────────────────────────────────────────────────

function sanitizePayload(data: any): any {
  if (!data || typeof data !== 'object') return data;
  const out: any = {};
  for (const k of Object.keys(data)) {
    const v = data[k];
    if (v === DELETE_FIELD_SENTINEL) {
      out[k] = null;
    } else if (v && typeof v === 'object' && '__increment' in v) {
      // Increments aren't atomic here — caller usually fetches+writes, so
      // best we can do is leave the field alone unless they pass a numeric.
      // Skipping this field is safer than silently corrupting the value.
      console.warn('[firestoreCompat] increment() is not atomic in shim mode');
      continue;
    } else {
      out[k] = v;
    }
  }
  return out;
}

/** addDoc always returns a DocRef whose id is guaranteed non-empty. */
export interface AddDocRef extends DocRef { id: string }

export async function addDoc(coll: CollRef, data: any): Promise<AddDocRef> {
  const table = tableFor(coll.name);
  const { data: row, error } = await supabase
    .from(table)
    .insert(sanitizePayload(data))
    .select('id')
    .single();
  if (error) throw error;
  return new DocRef(coll.name, (row as any).id) as AddDocRef;
}

export async function setDoc(ref: DocRef, data: any, _opts?: { merge?: boolean }): Promise<void> {
  const table = tableFor(ref.collection);
  if (!ref.id) {
    const { error } = await supabase.from(table).insert(sanitizePayload(data));
    if (error) throw error;
    return;
  }
  const merge = !!_opts?.merge;
  if (merge) {
    // Upsert merge — try update, fall back to insert.
    const { error: upErr } = await supabase
      .from(table)
      .update(sanitizePayload(data))
      .eq('id', ref.id);
    if (upErr) {
      const { error: inErr } = await supabase
        .from(table)
        .insert({ id: ref.id, ...sanitizePayload(data) });
      if (inErr) throw inErr;
    }
  } else {
    const { error } = await supabase
      .from(table)
      .upsert({ id: ref.id, ...sanitizePayload(data) });
    if (error) throw error;
  }
}

export async function updateDoc(ref: DocRef, data: any): Promise<void> {
  const { error } = await supabase
    .from(tableFor(ref.collection))
    .update(sanitizePayload(data))
    .eq('id', ref.id);
  if (error) throw error;
}

export async function deleteDoc(ref: DocRef): Promise<void> {
  const { error } = await supabase.from(tableFor(ref.collection)).delete().eq('id', ref.id);
  if (error) throw error;
}

// ─── Realtime ──────────────────────────────────────────────────────────────

export function onSnapshot(source: DocRef, callback: (snap: DocSnap) => void, _onError?: (e: any) => void): Unsubscribe;
export function onSnapshot(source: CollRef | Q, callback: (snap: QuerySnap) => void, _onError?: (e: any) => void): Unsubscribe;
export function onSnapshot(source: CollRef | Q | DocRef, callback: any, _onError?: (e: any) => void): Unsubscribe {
  let cancelled = false;

  if (source instanceof DocRef) {
    const ref = source;
    const reload = async () => {
      if (cancelled || !ref.id) return;
      try {
        const snap = await getDoc(ref);
        if (!cancelled) callback(snap);
      } catch (err) {
        if (_onError) _onError(err);
      }
    };
    void reload();
    const channel = supabase
      .channel(`fsc_doc_${ref.collection}_${ref.id}_${Date.now()}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: tableFor(ref.collection), filter: `id=eq.${ref.id}` }, () => { void reload(); })
      .subscribe();
    return () => { cancelled = true; void supabase.removeChannel(channel); };
  }

  const q = source instanceof Q ? source : new Q(source.name);
  const reload = async () => {
    if (cancelled) return;
    try {
      const snap = await getDocs(q);
      if (!cancelled) callback(snap);
    } catch (err) {
      if (_onError) _onError(err);
    }
  };
  void reload();
  const channel = supabase
    .channel(`fsc_col_${q.collection}_${Date.now()}`)
    .on('postgres_changes', { event: '*', schema: 'public', table: tableFor(q.collection) }, () => { void reload(); })
    .subscribe();
  return () => { cancelled = true; void supabase.removeChannel(channel); };
}

// ─── Transactions / batches — best-effort sequential fallback ──────────────

export async function runTransaction<T>(_db: any, fn: (tx: any) => Promise<T>): Promise<T> {
  // No real transaction support without an RPC. Best-effort: run reads then
  // writes sequentially. Race conditions are possible; callers that need
  // strict atomicity should be migrated to a custom Supabase RPC.
  const tx = {
    get: (ref: DocRef) => getDoc(ref),
    set: (ref: DocRef, data: any, opts?: any) => setDoc(ref, data, opts),
    update: (ref: DocRef, data: any) => updateDoc(ref, data),
    delete: (ref: DocRef) => deleteDoc(ref),
  };
  return fn(tx);
}

export function writeBatch(_db: any) {
  const ops: Array<() => Promise<void>> = [];
  return {
    set: (ref: DocRef, data: any, opts?: any) => { ops.push(() => setDoc(ref, data, opts)); },
    update: (ref: DocRef, data: any) => { ops.push(() => updateDoc(ref, data)); },
    delete: (ref: DocRef) => { ops.push(() => deleteDoc(ref)); },
    commit: async () => { for (const op of ops) await op(); },
  };
}

// ─── No-op exports kept for import-shape compatibility ─────────────────────

export const FieldValue = { delete: deleteField, serverTimestamp };
